import { Route } from '@angular/router';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import { InitialDataResolver } from 'app/app.resolvers';
import { Error404Component } from 'app/modules/admin/pages/error/error-404/error-404.component';

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [
    // Redirect empty path to '/example'
    { path: '', pathMatch: 'full', redirectTo: 'landing-home' }, // en redirect antes estaba example

    // Redirect signed-in user to the '/example'
    //
    // After the user signs in, the sign-in page will redirect the user to the 'signed-in-redirect'
    // path. Below is another redirection for that path to redirect the user to the desired
    // location. This is a small convenience to keep all main routes together here on this file.
    {
        path: 'signed-in-redirect',
        pathMatch: 'full',
        redirectTo: 'landing-home',
    }, // en redirect antes estaba example

    // Auth routes for guests
    {
        path: '',
        canMatch: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'confirmation-required',
                loadChildren: () =>
                    import(
                        'app/modules/authentication/confirmation-required/confirmation-required.module'
                    ).then((m) => m.AuthConfirmationRequiredModule),
            },
            {
                path: 'forgot-password',
                loadChildren: () =>
                    import(
                        'app/modules/authentication/forgot-password/forgot-password.module'
                    ).then((m) => m.AuthForgotPasswordModule),
            },
            {
                path: 'reset-password',
                loadChildren: () =>
                    import(
                        'app/modules/authentication/reset-password/reset-password.module'
                    ).then((m) => m.AuthResetPasswordModule),
            },
            {
                path: 'sign-in',
                loadChildren: () =>
                    import(
                        'app/modules/authentication/sign-in/sign-in.module'
                    ).then((m) => m.AuthSignInModule),
            },
            {
                path: 'sign-up',
                loadChildren: () =>
                    import(
                        'app/modules/authentication/sign-up/sign-up.module'
                    ).then((m) => m.AuthSignUpModule),
            },
            {
                path: 'activate-account',
                loadChildren: () =>
                    import(
                        'app/modules/authentication/activate/activate.module'
                    ).then((m) => m.ActivateModule),
            },
        ],
    },

    // Auth routes for authenticated users
    {
        path: '',
        canMatch: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'sign-out',
                loadChildren: () =>
                    import(
                        'app/modules/authentication/sign-out/sign-out.module'
                    ).then((m) => m.AuthSignOutModule),
            },
            {
                path: 'unlock-session',
                loadChildren: () =>
                    import(
                        'app/modules/authentication/unlock-session/unlock-session.module'
                    ).then((m) => m.AuthUnlockSessionModule),
            },
            {
                path: 'profile',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/profile/profile.module'
                    ).then((m) => m.ProfileModule),
            },
        ],
    },

    // Landing routes
    {
        path: '',
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'home',
                loadChildren: () =>
                    import('app/modules/landing/home/home.module').then(
                        (m) => m.LandingHomeModule
                    ),
            },
        ],
    },

    // Admin routes
    {
        path: '',
        canMatch: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        children: [
            //{path: 'example', loadChildren: () => import('app/modules/admin/example/example.module').then(m => m.ExampleModule)},
            {
                path: 'landing-home',
                loadChildren: () =>
                    import('app/modules/landing/home/home.module').then(
                        (m) => m.LandingHomeModule
                    ),
            },
            // Settings
            {
                path: 'settings',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/settings/settings.module'
                    ).then((m) => m.SettingsModule),
            },
            // Users
            {
                path: 'users',
                loadChildren: () =>
                    import('app/modules/users/users.module').then(
                        (m) => m.UsersModule
                    ),
            },
            // Contacts
            {
                path: 'persons',
                loadChildren: () =>
                    import('app/modules/persons/persons.module').then(
                        (m) => m.PersonsModule
                    ),
            },
            // Products
            {
                path: 'products',
                loadChildren: () =>
                    import('app/modules/products/products.module').then(
                        (m) => m.ProductsModule
                    ),
            },
            // Packing
            {
                path: 'packing',
                loadChildren: () =>
                    import('app/modules/packing/packing.module').then(
                        (m) => m.PackingModule
                    ),
            },
            // Companies
            {
                path: 'companies',
                loadChildren: () =>
                    import('app/modules/companies/companies.module').then(
                        (m) => m.CompaniesModule
                    ),
            },
            // Dynamic Form
            {
                path: 'dynamicForm',
                loadChildren: () =>
                    import('app/modules/dynform/dynform.module').then(
                        (m) => m.DynamicFormModule
                    ),
            },
            // Contracts
            {
                path: 'contracts',
                loadChildren: () =>
                    import(
                        'app/modules/contracts/contrats/contracts.module'
                    ).then((m) => m.ContractsModule),
            },
            // Contract edit
            {
                path: 'contract/form',
                loadChildren: () =>
                    import(
                        'app/modules/contracts/contract-forms/contract-form.module'
                    ).then((m) => m.ContractFormModule),
            },
            {
                path: 'contract/form/:id',
                loadChildren: () =>
                    import(
                        'app/modules/contracts/contract-forms/contract-form.module'
                    ).then((m) => m.ContractFormModule),
            },
            // Contract Payments
            {
                path: 'payments',
                loadChildren: () =>
                    import(
                        'app/modules/contracts/contract-payments/contract-payments.module'
                    ).then((m) => m.ContractPaymentsModule),
            },
            {
                path: 'payments/:contractNumber',
                loadChildren: () =>
                    import(
                        'app/modules/contracts/contract-payments/contract-payments.module'
                    ).then((m) => m.ContractPaymentsModule),
            },
            // Shipment Batch
            {
                path: 'shipment-batch',
                loadChildren: () =>
                    import(
                        'app/modules/shipmentBatch/shipmentBatch.module'
                    ).then((m) => m.ShipmentBatchModule),
            },
            {
                path: 'shipment-batch/:id',
                loadChildren: () =>
                    import(
                        'app/modules/shipmentBatch/shipmentBatch.module'
                    ).then((m) => m.ShipmentBatchModule),
            },
            // Shipment Report
            {
                path: 'shipment-report',
                loadChildren: () =>
                    import(
                        'app/modules/shipment-report/shipment-report.module'
                    ).then((m) => m.ShipmentReportModule),
            },
        ],
    },
    { path: '404', component: Error404Component },
    { path: '**', redirectTo: '/404' },
];
